import { useLang } from 'hooks/lang';

import { actions } from 'entities';
import { useDirectNavigation } from 'routes';

import { Column, LabeledInput, Button, Label, Card } from 'components/Blocks';
import { GoBackButton } from 'components/Buttons';
import { MonetaryInput, getUserLabel } from 'components/Inputs';
import { EntitySelect } from 'components/Entities';
import { UploadFileDropzone, SingleFileUploadButton } from 'components/Uploads';

import { useFormControl, useToggleV2 } from 'hooks';

import { UploadedFile } from 'types';

function CreatePackage(): JSX.Element {
  const { t } = useLang();

  const [multiUploadLoading, multiUploadLoadingHandlers] = useToggleV2();

  const { state, dispatch, handleChange } = useFormControl({
    name: '',
    value: '',
    currency: '',
    complementaryFiles: [] as UploadedFile[],
    proofFile: null as null | UploadedFile,
    client: '',
    salesPerson: '',
  });

  const canCreate = Object.values(state).every(Boolean) && !multiUploadLoading;

  const createPackage = actions.invoice.createPackage.use({
    onSuccess: useDirectNavigation('packages'),
  });

  const executeCreation = async (): Promise<void> => {
    if (!canCreate) return;

    createPackage.execute(state);
  };

  return (
    <Column gap="12">
      <h2 className="flex items-center gap-2 text-3xl font-medium leading-none text-primary phone:text-xl">
        <GoBackButton to="packages" />

        {t('packages')}
      </h2>

      <Card flow="column" className="mx-auto w-full max-w-5xl shadow-lg" spacing="large" gap="6">
        <h3 className="tracking-tight' text-2xl font-semibold leading-none">
          {t('create-package')}
        </h3>

        <LabeledInput name="name" value={state.name} onChange={handleChange} label={t('name')} />

        <EntitySelect
          target="clients.list"
          id="client"
          labelKey="name"
          value={state.client}
          onChange={handleChange}
          label={t('client') || ''}
        />

        <EntitySelect
          id="salesPerson"
          labelKey="name"
          target="users.salesUsers"
          label={t('sales-person')!}
          value={state.salesPerson}
          onChange={handleChange}
          fullWidth
          formatLabel={getUserLabel}
        />

        <MonetaryInput
          label="Value"
          value={state.value}
          currency={state.currency}
          onChangeDirect={(value) => dispatch({ value })}
          onCurrencyChangeDirect={(currency) => dispatch({ currency })}
        />

        <Column gap="2">
          <Label>{t('proof-file')}</Label>

          <SingleFileUploadButton
            file={state.proofFile}
            setFile={(proofFile) => dispatch({ proofFile })}
            uploadPrefix={`packages`}
          />
        </Column>

        <Column gap="2">
          <Label>{t('extra-files')}</Label>

          <UploadFileDropzone
            files={state.complementaryFiles}
            onLoading={(loading) => multiUploadLoadingHandlers.setIsOn(loading)}
            setFiles={(action) =>
              dispatch({
                complementaryFiles: ({ complementaryFiles }) =>
                  typeof action === 'function' ? action(complementaryFiles) : action,
              })
            }
            uploadPrefix="packages"
          />
        </Column>

        <Button disabled={!canCreate} onClick={executeCreation} loading={createPackage.loading}>
          {t('create')}
        </Button>
      </Card>
    </Column>
  );
}

export default CreatePackage;
